import { SCAttributes } from "../generic/generic-sc-attributes";
import { IGenericAggregateResponse, AttributesValidation } from "../generic/generic-types";
import { IClientAccount, IClientAccountFromList } from "../utils/types";
import { IProps } from "./salescredit-types";

export function SalesCreditPropsChanged(
  oldProps: IProps,
  newProps: IProps
): boolean {
  if (newProps.userEmail !== oldProps.userEmail) {
    return true;
  }
  const isFiltersAvailable = CheckFiltersChanged(
    oldProps.filters,
    newProps.filters
  );
  if (isFiltersAvailable !== null) {
    return isFiltersAvailable;
  }

  const filterItemsWithString = [
    "bdrCommercialId",
    "bdrLegalId",
    "icId",
    "name",
    "value",
  ];
  for (const key of Object.keys(newProps.filters)) {
    if (!oldProps || !oldProps.filters) {
      return true;
    }
    if (!oldProps.filters[key]) {
      return true;
    }
    if (filterItemsWithString.indexOf(key) > -1) {
      if (oldProps.filters[key] !== newProps.filters[key]) {
        return true;
      }
      continue;
    }
    const newPropsArray = newProps.filters[key].sort() as string[];
    const oldPropsArray = oldProps.filters[key].sort() as string[];
    if (newPropsArray.toString() !== oldPropsArray.toString()) {
      return true;
    }
  }
  return false;
}
function CheckFiltersChanged(oldFilters: any, newFilters: any) {
  if ((oldFilters && !newFilters) || (!oldFilters && newFilters)) {
    return true;
  }
  if (!newFilters && !oldFilters) {
    return false;
  }
  if (!newFilters) {
    return true;
  }
  if (
    Object.keys(newFilters)
      .sort()
      .toString() !==
    Object.keys(oldFilters)
      .sort()
      .toString()
  ) {
    return true;
  }
  return null;
}

export function ConvertForwardFilter(
  filter: { [attribute: string]: string[] | string } | undefined
): { [attribute: string]: string[] } {
  if (filter === undefined || filter === {}) {
    return {};
  }

  const returnFilter: { [attribute: string]: string[] } = {};
  for (const key of Object.keys(filter)) {
    if (filter[key] === undefined) {
      continue;
    }
    if (key === "value") {
      const date = "Date";
      returnFilter[date] = [filter[key] as string];
      continue;
    }
    if (key === "salesben_sales" || key === "salesben_team") {
      returnFilter[key] = filter[key] as string[];
    }
  }
  return returnFilter;
}

function isLogicalAttributesFilterPresent(filters: {
  [attribute: string]: string[];
}): boolean {
  return (
    "logicalAttributesFilter" in filters &&
    filters["logicalAttributesFilter"].toString() !== ""
  );
};

function hasValue(value: string[]): boolean {
  return value !== undefined &&
  value !== null &&
  value.length > 0
}

function extractLogicalFilterKeys(logicalFilter: string | string[]): string[] {
  const regex = /\b(and|or)\b|\[|\]/gi;
  const cleanedLogicalFilter = (logicalFilter as string).replace(regex, "");
  return cleanedLogicalFilter.split(",").map(s => s.split(":")[0]);
};

export function areFiltersCompatible(filters: { [attribute: string]: string[] }): AttributesValidation {
  if (!isLogicalAttributesFilterPresent(filters)) {
    return {
      valid: true,
      attribute: undefined,
    };
  }

  for (const key of Object.keys(filters)) {
    if (key === "logicalAttributesFilter") {
      continue;
    } else {
      if(hasValue(filters[key])) {
        const logicalFilterKeys = extractLogicalFilterKeys(filters["logicalAttributesFilter"]);
        if (logicalFilterKeys.indexOf(key) !== -1) {
          return {
            valid: false,
            attribute: key,
          };
        }
      }
    }
  }

  return {
    valid: true,
    attribute: undefined,
  };
};

export function ConvertFilter(
  filter:
    | { [attribute: string]: string[] | string | IClientAccount[] }
    | undefined,
  forBudget = false
): { [attribute: string]: string[] } {
  if (filter === undefined || filter === {}) {
    return {};
  }
  const returnFilter: { [attribute: string]: string[] } = {};

  for (const key of Object.keys(filter)) {
    if (filter[key] === undefined) {
      continue;
    }
    if (CheckDateAndIcFilter(key, filter, returnFilter)) {
      continue;
    }
    // Step Group Search
    if (key === SCAttributes.SC_MARK_STEP_GROUP && !forBudget) {
      SetFilterForStepGroup(key, filter, returnFilter);
      continue;
    }
    returnFilter[key] = filter[key] as string[];
  }

  if (
    returnFilter[SCAttributes.SC_MARK_STEP_GROUP] === undefined &&
    !forBudget
  ) {
    returnFilter[SCAttributes.SC_MARK_STEP_GROUP] = ["1", "2", "3", "4"];
  }
  return returnFilter;
}

function CheckDateAndIcFilter(key: any, filter: any, returnFilter: any) {
  if (key === "bdrLegalId" || key === "icId" || key === "name") {
    return true;
  }
  if (key === "bdrCommercialId") {
    returnFilter[SCAttributes.SC_CLIENT_GEN_ID] = [filter[key] as string];
    return true;
  }
  if (key === "value") {
    const date = "Date";
    returnFilter[date] = [filter[key] as string];
    return true;
  }
  if (key === "accounts") {
    for (const account of filter[key] as IClientAccount[]) {
      if (!returnFilter[SCAttributes.SC_CLIENT_GEN_ID]) {
        returnFilter[SCAttributes.SC_CLIENT_GEN_ID] = [];
      }
      returnFilter[SCAttributes.SC_CLIENT_GEN_ID].push(account.bdrCommercialId);
    }
    return true;
  }
  if (key === "accountsList" && filter[key] !== null) {
    for (const account of filter[key] as IClientAccountFromList[]) {
      if (!returnFilter[SCAttributes.SC_CLIENT_GEN_ID]) {
        returnFilter[SCAttributes.SC_CLIENT_GEN_ID] = [];
      }
      returnFilter[SCAttributes.SC_CLIENT_GEN_ID].push(
        account.bdrId.bdrCommercialId.id
      );
    }
    return true;
  }
  return false;
}
function SetFilterForStepGroup(key: any, filter: any, returnFilter: any) {
  if (filter[key][0] === "4") {
    returnFilter[SCAttributes.SC_MARK_STEP_GROUP] = ["1", "2", "3", "4"];
  }
  if (filter[key][0] === "3") {
    returnFilter[SCAttributes.SC_MARK_STEP_GROUP] = ["1", "2", "3"];
  }
  if (filter[key][0] === "2") {
    returnFilter[SCAttributes.SC_MARK_STEP_GROUP] = ["1", "2"];
  }
  if (filter[key][0] === "1") {
    returnFilter[SCAttributes.SC_MARK_STEP_GROUP] = ["1"];
  }
}
export function ValidateGenericData(result: IGenericAggregateResponse) {
  if (
    result === undefined ||
    result.aggregates === undefined ||
    result.aggregates.length === 0
  ) {
    return false;
  }
  return true;
}
